import * as React from 'react';
import { connect } from 'react-redux';
import { goBack } from 'react-router-redux';
import { find } from 'lodash';
import { getVehiclesForVehicleType } from '../../redux/actions/vehicleTypes';
import {
  getBooking,
  getBookingVehicles,
  assignBooking,
} from '../../redux/actions/bookings';
import {
  IconButton,
  Notification,
  BookingAssignForm,
  RouteWrapper,
  HeadingSection,
  Section,
} from '../../components';

interface Props {
  match: any;
  dispatch: Function;
}

interface State {
  id: number;
  booking: any;
  error: string | boolean;
  formError: string | boolean;
  isReady: boolean;
  vehicle?: any;
  vehicles?: Array<any>;
}

class BookingAssign extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      id: this.props.match.params.id,
      booking: false,
      error: false,
      formError: false,
      isReady: false,
      vehicles: [],
    };
  }

  /**
   * Load booking data and vehicles available for booking
   */
  async componentDidMount() {
    const { data, error } = await this.props.dispatch(
      getBooking(this.state.id),
    );
    const booking = data;

    // Throw error if there was an error retrieving booking
    if (error) {
      this.setState({ error });
      return;
    }

    // Throw an error if this booking cannot be assigned a vehicle
    if (booking.status !== 'UNASSIGNED') {
      this.setState({
        error: 'This booking is not applicable for the desired action',
      });
      return;
    }

    // Retrieve the available vehicles for this booking
    const [availableVehicles, allVehicles] = await Promise.all([
      await this.props.dispatch(getBookingVehicles(this.state.id)),
      await this.props.dispatch(
        getVehiclesForVehicleType(booking.vehicle_type_id),
      ),
    ]);

    this.setState({
      booking,
      // If there were no errors retrieving the vehicles set them to disabled if
      // they are not available
      vehicles: !availableVehicles.error
        ? allVehicles.data.map((vehicle) => {
            if (!find(availableVehicles.data, { id: vehicle.id })) {
              return {
                disabled: true,
                ...vehicle,
              };
            }
            return vehicle;
          })
        : [],
      error: availableVehicles.error,
      isReady: true,
    });
  }

  render() {
    return (
      <RouteWrapper
        title="Assign Vehicle"
        requireAuth
        requirePermission="MANAGE_BOOKINGS"
        breadcrumbs={[
          {
            link: `/bookings/status/${this.state.booking.status}`,
            text: `Bookings`,
          },
          {
            link: `/bookings/${this.state.id}`,
            text: this.state.booking.invoiceId,
          },
          {
            link: `/bookings/${this.state.id}/assign`,
            text: 'Assign Vehicle',
          },
        ]}
        error={this.state.error}
        ready={this.state.isReady}
      >
        <HeadingSection
          title="Assign Vehicle"
          controls={[
            <IconButton
              icon="undo"
              className="is-default"
              handleClick={() => {
                this.props.dispatch(goBack());
              }}
              key="return"
            >
              Return
            </IconButton>,
          ]}
        />
        <Section>
          <div className="columns is-centered">
            <div className="column is-12-desktop is-7-widescreen">
              <BookingAssignForm
                showHeading={false}
                values={{
                  vehicle_id: this.state.vehicles.map((vehicle) => ({
                    name: `${vehicle.registration}${
                      vehicle.disabled ? ' (unavailable)' : ''
                    }${vehicle.needs_service ? ' (needs service)' : ''}${
                      !vehicle.insured ? ' (not insured)' : ''
                    }`,
                    value: vehicle.id,
                    disabled: vehicle.disabled,
                  })),
                }}
                onChange={({ vehicle_id }, changeValue) => {
                  const vehicle = find(this.state.vehicles, {
                    id: Number(vehicle_id) as any,
                  }) || { mileage: '' };
                  if (this.state.vehicle === vehicle) {
                    return;
                  }
                  changeValue('update.mileage', vehicle.mileage);
                  this.setState({ vehicle });
                }}
                onSubmit={(id, data) => async (dispatch) => {
                  const response = await dispatch(
                    assignBooking(this.state.id, {
                      vehicle_id: this.state.vehicle.id,
                      mileage: data.mileage,
                    }),
                  );
                  if (response.error) {
                    this.setState({ formError: response.error });
                    return;
                  }
                  return dispatch(goBack());
                }}
              />
              {this.state.formError && (
                <Notification type="danger">
                  {this.state.formError}
                </Notification>
              )}
            </div>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

export default connect()(BookingAssign);
